import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'modules/common/components/Modal/Modal';
import masterServicesStrings from 'modules/masterServices/strings';
import { formValidationErrorType } from 'modules/common/enums';
import SubHeader from 'modules/common/components/SubHeader/SubHeader';
import AddOrEditMasterServiceComponent from 'modules/masterServices/components/AddOrEditMasterServiceComponent';
import {
  addOrEditMasterService as addOrEditMasterServiceAction,
  deleteMasterService as deleteMasterServiceAction,
  fetchMasterServicesList,
  resetAddOrEditMasterService,
  resetDeleteMasterService,
} from 'modules/masterServices/actions';
import { status } from 'constants/general';
import Loader from 'modules/common/components/Loader/Loader';
import MasterServicesList from 'modules/masterServices/components/MasterServicesList/MasterServicesList';
import Pagination from 'modules/common/components/Pagination/Pagination';
import MasterServicesFilter from 'modules/masterServices/components/MasterServicesFilter/MasterServicesFilter';
import {
  ErrorTextWrapper,
  LoaderWrapper,
  MasterServiceDeleteConfirmTitle,
  MasterServicesWrapper,
  NoResultsText,
  NoResultsWrapper,
  PaginationWrapper,
  extendDeleteConfirmModalBtn,
} from './MasterServicesContainer.style';
// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const requiredFormFields = ['englishName', 'arabicName', 'status'];
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const MasterServicesContainer = ({ initialMasterServicesListApiCallBody }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { masterServicesList, addOrEditMasterService, deleteMasterService, subContext } =
    useSelector(state => ({
      masterServicesList: state.masterServices.masterServicesList,
      addOrEditMasterService: state.masterServices.addOrEditMasterService,
      deleteMasterService: state.masterServices.deleteMasterService,
      subContext: state.common.subContext,
    }));

  const totalResultsCount = masterServicesList?.details?.totalCount;
  const numberOfPages = (totalResultsCount && Math.ceil(totalResultsCount / 10)) || 1;
  const masterServicesListArr = masterServicesList?.details?.results;

  const dispatch = useDispatch();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [masterServicesListApiCallBody, setMasterServicesListApiCallBody] = useState({});
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [formFieldsValues, setFormFieldsValues] = useState({
    englishName: '',
    arabicName: '',
    categoryKey: subContext,
    status: 'active',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    englishName: formValidationErrorType.valid,
    arabicName: formValidationErrorType.valid,
    status: formValidationErrorType.valid,
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMasterService, setSelectedMasterService] = useState(null);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    setFormFieldsValues(prevState => ({
      ...prevState,
      categoryKey: subContext,
    }));

    const data = {
      ...initialMasterServicesListApiCallBody,
    };
    data.categoryKey = subContext;
    if (data.searchTerm) delete data.searchTerm;
    document.getElementById('search-bar-input').value = '';
    setMasterServicesListApiCallBody({ ...data });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subContext]);

  useEffect(() => {
    if (Object.keys(masterServicesListApiCallBody).length > 0) {
      const data = {
        ...masterServicesListApiCallBody,
      };
      const body = {
        data,
      };
      dispatch(fetchMasterServicesList(body));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, masterServicesListApiCallBody]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleConfirmDetailsModalClick = functionCallBack => {
    let isFormValid = true;

    // we get all required fields and then make sure it has a value
    // and if it has a special regex requirement we validate it against that regex
    requiredFormFields.forEach(fieldName => {
      let isFieldValid = formValidationErrorType.valid;

      if (!formFieldsValues[fieldName].toString()) {
        isFieldValid =
          (!formFieldsValues[fieldName] && formValidationErrorType.empty) ||
          formValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: isFieldValid,
      }));
    });

    if (isFormValid === false) return;

    const actionBody = {
      data: {
        MasterServiceNameAr: formFieldsValues.arabicName,
        MasterServiceNameEn: formFieldsValues.englishName,
        categoryKey: formFieldsValues.categoryKey,
        StatusKey: formFieldsValues.status,
        ...(selectedMasterService?.masterServiceKey
          ? { MasterServiceKey: selectedMasterService.masterServiceKey }
          : {}),
      },
      callbackAction: functionCallBack,
    };
    dispatch(addOrEditMasterServiceAction(actionBody));
  };

  const handleCancelDetailsModalClick = () => {
    setFormFieldsValidation({
      englishName: formValidationErrorType.valid,
      arabicName: formValidationErrorType.valid,
      categoryKey: formValidationErrorType.valid,
      status: formValidationErrorType.valid,
    });
    setFormFieldsValues({
      englishName: '',
      arabicName: '',
      categoryKey: subContext,
      status: 'active',
    });
    setSelectedMasterService(null);
    setIsDetailsModalOpen(false);
    dispatch(resetAddOrEditMasterService());
  };

  const handleEditMasterServiceBtnClick = masterService => {
    const { masterServiceNameEn, masterServiceNameAr, categoryKey, statusKey } = masterService;
    setFormFieldsValues({
      englishName: masterServiceNameEn,
      arabicName: masterServiceNameAr,
      categoryKey,
      status: statusKey,
    });
    setSelectedMasterService(masterService);
    setIsDetailsModalOpen(true);
  };

  const handleDeleteMasterServiceBtnClick = masterService => {
    setSelectedMasterService(masterService);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteMasterServiceConfirmBtnClick = functionCallBack => {
    const actionBody = {
      data: selectedMasterService,
      callbackAction: functionCallBack,
    };
    dispatch(deleteMasterServiceAction(actionBody));
  };

  const handleCancelDeleteModalClick = () => {
    setSelectedMasterService(null);
    setIsDeleteModalOpen(false);
    dispatch(resetDeleteMasterService());
  };

  const handleOrderPaginationClick = selectedPageNumber => {
    const data = {
      ...masterServicesListApiCallBody,
    };
    data.pageNum = selectedPageNumber;
    setMasterServicesListApiCallBody({ ...data });
  };

  const submitSearchQuery = (value, reset) => {
    const data = {
      ...masterServicesListApiCallBody,
      pageNum: 1,
    };
    if (value) data.searchTerm = value;
    if (reset) delete data.searchTerm;
    setMasterServicesListApiCallBody({ ...data });
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        submitSearchQuery(value);
      }, 300);
    } else if (value.length < searchQueryMinLength && masterServicesListApiCallBody.searchTerm) {
      submitSearchQuery(value, true);
    }
  };

  const handleDropdownInputChange = value => {
    const data = {
      ...masterServicesListApiCallBody,
      pageNum: 1,
    };
    if (value) data.statuskey = value;
    if (value === 'all') delete data.statuskey;
    setMasterServicesListApiCallBody({ ...data });
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  const renderMasterServicesList = () => {
    if (
      masterServicesList.status === status.SHOULD_CALL_API ||
      masterServicesList.status === status.FETCHING
    )
      return (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      );

    if (masterServicesList.status === status.SUCCESS && masterServicesListArr?.length > 0)
      return (
        <>
          <MasterServicesList
            masterServicesListArr={masterServicesListArr}
            handleEditMasterServiceBtnClick={handleEditMasterServiceBtnClick}
            handleDeleteMasterServiceBtnClick={handleDeleteMasterServiceBtnClick}
          />
          {numberOfPages > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={masterServicesListApiCallBody.pageNum}
                totalCount={numberOfPages}
                handleOrderPaginationClick={handleOrderPaginationClick}
              />
            </PaginationWrapper>
          )}
        </>
      );

    return (
      <NoResultsWrapper>
        <NoResultsText>
          {masterServicesStrings.masterServicesList.masterServicesGridNoResultFound}
        </NoResultsText>
      </NoResultsWrapper>
    );
  };
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <MasterServicesWrapper>
      <SubHeader
        subHeaderTitle="Master Service"
        totalCount={totalResultsCount}
        handleAddBtnClick={() => setIsDetailsModalOpen(true)}
        addBtnText={masterServicesStrings.masterServiceDetailsContent.addMasterService}
      />
      <MasterServicesFilter
        handleSearchBarInputChange={handleSearchBarInputChange}
        handleDropdownInputChange={handleDropdownInputChange}
        masterServicesListApiCallBody={masterServicesListApiCallBody}
      />
      {renderMasterServicesList()}
      <Modal
        data-testid="AddOrEditMasterServiceModal"
        title={
          selectedMasterService !== null
            ? masterServicesStrings.masterServiceDetailsContent.editMasterService
            : masterServicesStrings.masterServiceDetailsContent.addMasterService
        }
        onClose={() => handleCancelDetailsModalClick()}
        toggle={isDetailsModalOpen}
        onConfirmHandler={() => {
          handleConfirmDetailsModalClick(() => {
            handleCancelDetailsModalClick();
            const data = {
              ...initialMasterServicesListApiCallBody,
              pageNum: masterServicesListApiCallBody.pageNum,
              categoryKey: subContext,
            };
            setMasterServicesListApiCallBody({ ...data });
          });
        }}
        confirmActionLabel={masterServicesStrings.masterServiceDetailsContent.confirmButtonLabel}
        confirmActionLoading={addOrEditMasterService.status === status.FETCHING}
        onCancelHandler={() => handleCancelDetailsModalClick()}
        cancelActionLabel={masterServicesStrings.masterServiceDetailsContent.cancelButtonLabel}
      >
        <AddOrEditMasterServiceComponent
          formFieldsValues={formFieldsValues}
          setFormFieldsValues={setFormFieldsValues}
          formFieldsValidation={formFieldsValidation}
        />
        {addOrEditMasterService?.error && (
          <ErrorTextWrapper>Error: {addOrEditMasterService?.error}</ErrorTextWrapper>
        )}
      </Modal>
      <Modal
        data-testid="DeleteMasterServiceModal"
        title={masterServicesStrings.masterServiceDelete.masterServiceDeleteModalTitle}
        onClose={() => handleCancelDeleteModalClick()}
        toggle={isDeleteModalOpen}
        onConfirmHandler={() =>
          handleDeleteMasterServiceConfirmBtnClick(() => {
            handleCancelDeleteModalClick();
            const data = {
              ...initialMasterServicesListApiCallBody,
              categoryKey: subContext,
            };
            if (masterServicesListArr?.length > 1)
              data.pageNum = masterServicesListApiCallBody.pageNum;
            else if (masterServicesListApiCallBody.pageNum > 1)
              data.pageNum = masterServicesListApiCallBody.pageNum - 1;
            setMasterServicesListApiCallBody({ ...data });
          })
        }
        confirmActionLabel={
          masterServicesStrings.masterServiceDelete.masterServiceDeleteModalConfirmBtn
        }
        confirmActionLoading={deleteMasterService.status === status.FETCHING}
        extendConfirmButtonStyle={extendDeleteConfirmModalBtn}
        onCancelHandler={() => handleCancelDeleteModalClick()}
        cancelActionLabel={masterServicesStrings.masterServiceDetailsContent.cancelButtonLabel}
      >
        <MasterServiceDeleteConfirmTitle>
          {masterServicesStrings.masterServiceDelete.masterServiceDeleteModalConfirmTitle(
            selectedMasterService?.masterServiceNameEn,
          )}
        </MasterServiceDeleteConfirmTitle>
        {deleteMasterService?.error && (
          <ErrorTextWrapper>Error: {deleteMasterService?.error}</ErrorTextWrapper>
        )}
      </Modal>
    </MasterServicesWrapper>
  );
};
// ###############################################################
// <----------------------- End Return UI ----------------------->
// ###############################################################

MasterServicesContainer.propTypes = {
  initialMasterServicesListApiCallBody: PropTypes.shape({}).isRequired,
};

export default MasterServicesContainer;
